"use client";

import { useEffect, useRef } from "react";
import type { FilterInput } from "@/services/entity-instance.service";
import {
  getSubscriptionManager,
  type EntitySubscriptionHandle,
  type EventMembership,
} from "@/lib/subscriptions/subscription-manager";
import type { EntityEventKind } from "@/services/subscription.service";

/**
 * Registers a live subscription for `entityName` on the shared websocket and
 * calls `onEvent` for every create/update/delete the backend delivers. The
 * subscription is scoped to `filter` and projects `fieldsSelection` (which
 * should match the fields the caller renders, so event payloads slot straight
 * into the local cache).
 *
 * Filter/fields changes update the subscription in place (same server id) rather
 * than tearing it down. `onEvent` is held in a ref so a new closure each render
 * doesn't churn the subscription.
 */
export interface UseEntitySubscriptionArgs {
  entityName: string;
  fieldsSelection: string;
  filter?: FilterInput;
  onCreate?: boolean;
  onUpdate?: boolean;
  onDelete?: boolean;
  onEvent: (
    kind: EntityEventKind,
    data: Record<string, unknown>,
    membership?: EventMembership,
  ) => void;
  /** Called after a reconnect+reregister so the caller can backfill missed events. */
  onResync?: () => void;
  enabled?: boolean;
}

export function useEntitySubscription({
  entityName,
  fieldsSelection,
  filter,
  onCreate = true,
  onUpdate = true,
  onDelete = true,
  onEvent,
  onResync,
  enabled = true,
}: UseEntitySubscriptionArgs) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const onResyncRef = useRef(onResync);
  onResyncRef.current = onResync;

  const handleRef = useRef<EntitySubscriptionHandle | null>(null);
  // Serialize filter changes: the subscribe promise resolves after the initial
  // registration, at which point we apply the latest filter/fields.
  const filterKey = JSON.stringify(filter ?? null);

  // Latest filter/fields every render, so a subscribe() that resolves late can
  // catch up on changes the update effect dropped while no handle existed yet
  // (e.g. relation columns joining fieldsToFetch once permissions load).
  const latestArgsRef = useRef({ filter, fieldsSelection, filterKey });
  latestArgsRef.current = { filter, fieldsSelection, filterKey };

  useEffect(() => {
    if (!enabled || !entityName || !fieldsSelection) return;

    let cancelled = false;
    const manager = getSubscriptionManager();
    const subscribedFieldsSelection = fieldsSelection;
    const subscribedFilterKey = filterKey;

    manager
      .subscribe({
        entityName,
        fieldsSelection,
        filter,
        onCreate,
        onUpdate,
        onDelete,
        onEvent: (kind, data, membership) => onEventRef.current(kind, data, membership),
        onResync: () => onResyncRef.current?.(),
      })
      .then((handle) => {
        if (cancelled) {
          handle.unsubscribe();
          return;
        }
        handleRef.current = handle;
        // Filter/fields may have changed while subscribe() was in flight — the
        // update effect below no-ops without a handle, so flush the latest
        // values now or the subscription would keep the stale projection.
        const latest = latestArgsRef.current;
        if (
          latest.fieldsSelection !== subscribedFieldsSelection ||
          latest.filterKey !== subscribedFilterKey
        ) {
          handle
            .update({ filter: latest.filter, fieldsSelection: latest.fieldsSelection })
            .catch((error) => {
              console.error("[subscriptions] update failed", error);
            });
        }
      })
      .catch((error) => {
        console.error("[subscriptions] subscribe failed", error);
      });

    return () => {
      cancelled = true;
      handleRef.current?.unsubscribe();
      handleRef.current = null;
    };
    // Re-subscribe only when the identity of the subscription changes. Filter and
    // fields updates are applied via the separate effect below (in place).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityName, onCreate, onUpdate, onDelete]);

  useEffect(() => {
    if (!handleRef.current) return;
    handleRef.current.update({ filter, fieldsSelection }).catch((error) => {
      console.error("[subscriptions] update failed", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, fieldsSelection]);
}
